import React from 'react';
import { motion } from 'framer-motion';
import { Check, Calendar, Info, Eye, Sparkles, Heart } from 'lucide-react';
import { tarotReadings, tarotPageCopy, siteConfig } from '../data/mock';
import { SEO, pageSEO } from '../components/SEO';

export const TarotPage = () => {
  const handleBookReading = (reading) => {
    window.open(`${siteConfig.calendlyUrl}/${reading.calendlyEvent}`, '_blank');
  };

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO {...pageSEO.tarot} path="/tarot" />
      {/* SECTION 1 — OPENING */}
      <section className="relative min-h-[80vh] flex items-center pt-32 pb-24">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1696562535437-d542584811bf?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-charcoal)] via-[var(--color-charcoal)]/90 to-[var(--color-charcoal)]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-transparent to-[var(--color-charcoal)]/50" />
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-6 block">
              {tarotPageCopy.opening.subheadline}
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mb-4 leading-[1.05]">
              {tarotPageCopy.opening.headline.split(',')[0]}
              <span className="text-[var(--color-deep-red-light)]">
                {tarotPageCopy.opening.headline.includes(',') ? `, ${tarotPageCopy.opening.headline.split(',')[1]}` : ''}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-relaxed max-w-3xl mb-6 mt-8">
              {tarotPageCopy.opening.intro}
            </p>
            <p className="text-lg text-[var(--color-cream)]/60 leading-relaxed max-w-3xl">
              {tarotPageCopy.opening.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — READING PHILOSOPHY */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal-light)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              The Approach
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              {tarotPageCopy.philosophy.headline}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tarotPageCopy.philosophy.points.map((point, index) => {
              const icons = [Eye, Sparkles, Heart];
              const Icon = icons[index];
              const colors = ['deep-red', 'midnight-blue', 'bottle-green'];
              const color = colors[index];
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className={`p-8 bg-[var(--color-charcoal)] rounded-lg border-t-2 ${
                    color === 'deep-red'
                      ? 'border-[var(--color-deep-red)]'
                      : color === 'midnight-blue'
                      ? 'border-[var(--color-midnight-blue)]'
                      : 'border-[var(--color-bottle-green)]'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-6 ${
                    color === 'deep-red'
                      ? 'bg-[var(--color-deep-red)]/20'
                      : color === 'midnight-blue'
                      ? 'bg-[var(--color-midnight-blue)]/20'
                      : 'bg-[var(--color-bottle-green)]/20'
                  }`}>
                    <Icon className="w-5 h-5 text-[var(--color-cream)]/70" />
                  </div>
                  <h3 className="font-serif text-xl mb-4 text-[var(--color-gold)]">
                    {point.title}
                  </h3>
                  <p className="text-[var(--color-cream)]/60 leading-relaxed">
                    {point.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — TAROT SESSIONS & PRICING */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Sessions
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              Choose Your Session
            </h2>
            <p className="text-lg text-[var(--color-cream)]/70 max-w-2xl mx-auto">
              Each session type is designed for different depths of inquiry. Choose what resonates with where you are.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tarotReadings.map((reading, index) => (
              <motion.div
                key={reading.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className={`relative p-8 rounded-lg transition-all duration-300 ${
                  index === 1
                    ? 'bg-[var(--color-deep-red)]/10 border border-[var(--color-deep-red)]/30'
                    : 'bg-[var(--color-charcoal-light)] border border-[var(--color-dark-purple)]/30'
                }`}
              >
                {index === 1 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-[var(--color-deep-red)] text-[var(--color-cream)] text-xs tracking-[0.15em] uppercase rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="font-serif text-2xl mb-1">{reading.name}</h3>
                  <p className="text-[var(--color-gold)] text-sm">{reading.duration}</p>
                </div>
                
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-sm text-[var(--color-cream)]/60">{reading.currency}</span>
                  <span className="text-4xl font-serif text-[var(--color-cream)]">
                    {reading.price}
                  </span>
                </div>
                
                <p className="text-[var(--color-cream)]/60 mb-8 leading-relaxed text-sm">
                  {reading.description}
                </p>
                
                <div className="space-y-3 mb-8">
                  {reading.includes.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[var(--color-gold)] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[var(--color-cream)]/70">{item}</span>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => handleBookReading(reading)}
                  className={`w-full py-4 rounded-full font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    index === 1
                      ? 'bg-[var(--color-deep-red)] text-[var(--color-cream)] hover:bg-[var(--color-deep-red-light)]'
                      : 'border border-[var(--color-gold)] text-[var(--color-gold)] hover:bg-[var(--color-gold)] hover:text-[var(--color-charcoal)]'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Book This Session
                </button>
              </motion.div>
            ))}
          </div>

          {/* Payment Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 max-w-2xl mx-auto text-center"
          >
            <p className="text-sm text-[var(--color-cream)]/50 leading-relaxed">
              After scheduling your session, you'll receive a secure payment link. Payment confirms your booking. All sessions are conducted via video call.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4 — WHAT YOU RECEIVE */}
      <section className="py-24 lg:py-32 bg-[var(--color-dark-purple)]/15">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Included
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              {tarotPageCopy.whatYouReceive.headline}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {tarotPageCopy.whatYouReceive.items.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-6 bg-[var(--color-charcoal)] rounded-lg border border-[var(--color-dark-purple)]/30"
              >
                <div className="w-8 h-8 rounded-full bg-[var(--color-gold)]/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[var(--color-gold)]" />
                </div>
                <p className="text-[var(--color-cream)]/80 leading-relaxed">{item}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 — BOOKING PROCESS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              How It Works
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              The Booking Process
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                step: '01',
                title: 'Choose Your Session',
                description: 'Select the session type that feels right for your current inquiry or need.'
              },
              {
                step: '02',
                title: 'Schedule Your Time',
                description: 'You\'ll be directed to select a time that works for you from available slots.'
              },
              {
                step: '03',
                title: 'Complete Payment',
                description: 'After scheduling, you\'ll receive a secure payment link to confirm your booking.'
              },
              {
                step: '04',
                title: 'Prepare & Receive',
                description: 'You\'ll receive session details, video call link, and optional pre-session reflection prompts within 24 hours.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 p-6 bg-[var(--color-charcoal-light)] rounded-lg"
              >
                <div className="flex-shrink-0">
                  <span className="font-serif text-2xl text-[var(--color-gold)]">{item.step}</span>
                </div>
                <div>
                  <h3 className="font-serif text-xl mb-2">{item.title}</h3>
                  <p className="text-[var(--color-cream)]/60">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — ETHICAL NOTE */}
      <section className="py-16 bg-[var(--color-charcoal-light)]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex gap-5 p-8 bg-[var(--color-charcoal)] rounded-lg border border-[var(--color-dark-purple)]/30"
          >
            <Info className="w-6 h-6 text-[var(--color-gold)] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-serif text-lg mb-3 text-[var(--color-cream)]">
                {tarotPageCopy.ethicalNote.headline}
              </h4>
              <p className="text-[var(--color-cream)]/60 text-sm leading-relaxed">
                {tarotPageCopy.ethicalNote.content}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
